import { useState } from "react";
import { NavLink, Outlet, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { BrandMark } from "../components/BrandMark";
import { HelpDrawer } from "../components/help/HelpDrawer";
import { chatApi } from "./api";
import { DecisionEmails } from "./DecisionEmails";
import { LangProvider, UI, useLang } from "./lang";

/**
 * The everyday surface (Change SRS v0.4 NEW-UX-1/2): one link, a header,
 * the content, three tabs. No left navigation, no settings, no tour. A
 * fourth tab needs a written reason in the decision register.
 */
function Shell() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const { dir, lang, setLang, t } = useLang();
  const queue = useQuery({
    queryKey: ["chat-queue", workspaceId],
    queryFn: () => chatApi.queue(workspaceId),
    enabled: !!workspaceId,
    refetchInterval: 30_000,
  });
  const open = queue.data?.open ?? 0;
  // The same query the thread uses, so this adds no request once it has loaded.
  const thread = useQuery({
    queryKey: ["chat-thread", workspaceId],
    queryFn: () => chatApi.thread(workspaceId),
    enabled: !!workspaceId,
  });
  const me = thread.data?.me;
  const [menuOpen, setMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // The console is for the people who run the workforce (audit fix 7).
  const runsTheConsole = me?.role === "cxo" || me?.role === "agent_handler";
  const tab = (to: string, label: { en: string; ar: string }, extra?: React.ReactNode) => (
    <NavLink
      to={`/app/${workspaceId}/${to}`}
      data-testid={`pwa-tab-${to}`}
      className={({ isActive }) => `pwa-tab${isActive ? " on" : ""}`}
    >
      <span>{t(label)}</span>
      {extra}
    </NavLink>
  );
  return (
    <div className="pwa" dir={dir} lang={lang} data-testid="pwa-shell">
      <header className="pwa-head">
        <BrandMark height={18} />
        <span className="pwa-spacer" />
        {runsTheConsole ? (
          <a className="pwa-console" href={`/workspaces/${workspaceId}/dashboard`}>
            {t(UI.openConsole)}
          </a>
        ) : null}
        <button type="button" className="pwa-help" data-testid="pwa-help" aria-label={t({ en: "Help", ar: "مساعدة" })}
          onClick={() => setHelpOpen(true)}>
          ?
        </button>
        {me ? (
          // On a phone the sidebar is hidden: who you are and Sign out live here.
          <div className="pwa-me-wrap">
            <button type="button" className="pwa-me-button" data-testid="pwa-me-button" aria-haspopup="menu"
              aria-expanded={menuOpen} aria-label={me.name} onClick={() => setMenuOpen((o) => !o)}>
              {me.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </button>
            {menuOpen ? (
              <div className="pwa-me-menu" role="menu" data-testid="pwa-me-menu">
                <div className="pwa-me-name">{me.name}</div>
                <div className="pwa-me-role">{t({ en: me.role_label, ar: me.role_label_ar ?? me.role_label })}</div>
                <DecisionEmails workspaceId={workspaceId} id="pwa-emails-menu" />
                <form method="post" action="/api/auth/logout">
                  <button type="submit" className="pwa-btn">{t(UI.signOut)}</button>
                </form>
              </div>
            ) : null}
          </div>
        ) : null}
        <button
          type="button"
          className="pwa-lang"
          data-testid="pwa-lang"
          aria-label={lang === "ar" ? "English" : "العربية"}
          onClick={() => setLang(lang === "ar" ? "en" : "ar")}
        >
          {t(UI.switchLang)}
        </button>
      </header>
      <HelpDrawer isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
      <main className="pwa-main">
        <Outlet />
      </main>
      <nav className="pwa-tabs" aria-label="Sections">
        {tab("thread", UI.thread)}
        {tab(
          "queue",
          UI.queue,
          open > 0 ? (
            <span className="pwa-badge" data-testid="pwa-queue-count">
              {open}
            </span>
          ) : null,
        )}
        {tab("reports", UI.reports)}
        {/* Shown in the wide-screen sidebar only; a phone keeps three tabs. */}
        <div className="pwa-side-foot">
          {me ? (
            <div className="pwa-me" data-testid="pwa-me">
              <span className="pwa-me-name">{me.name}</span>
              <span className="pwa-me-role">{t({ en: me.role_label, ar: me.role_label_ar ?? me.role_label })}</span>
            </div>
          ) : null}
          {me ? <DecisionEmails workspaceId={workspaceId} id="pwa-emails-side" /> : null}
          {runsTheConsole ? (
            <a className="pwa-signout" href={`/workspaces/${workspaceId}/dashboard`}>
              {t(UI.openConsole)}
            </a>
          ) : null}
          <form method="post" action="/api/auth/logout">
            <button type="submit" className="pwa-signout" data-testid="pwa-signout">
              {t(UI.signOut)}
            </button>
          </form>
        </div>
      </nav>
    </div>
  );
}

export function AppShellPwa() {
  return (
    <LangProvider>
      <Shell />
    </LangProvider>
  );
}
